import React, { useEffect, useState } from 'react';
import { getTagIconId, getTagIconMeta, type TagIconId } from '../../utils/tagIcons';
import {
  getTagCountryIso2List,
  subscribeTagAppearance,
} from '../../utils/tagAppearance';
import { CountryFlag } from '../stats/CountryFlag';

type TagColorChipProps = {
  tag?: string;
  iconId?: TagIconId;
  size?: 'sm' | 'md';
  className?: string;
};

/** Small color swatch for the tag color picker. */
export const TagColorChip: React.FC<TagColorChipProps> = ({
  tag,
  iconId,
  size = 'sm',
  className = '',
}) => {
  const meta = getTagIconMeta(iconId ?? getTagIconId(tag));
  const box = size === 'md' ? 'h-8 w-8' : 'h-5 w-5';

  return (
    <span
      className={`relative inline-flex ${box} shrink-0 overflow-hidden rounded-md ring-1 ring-inset ring-black/5 dark:ring-white/10 ${meta.chipClass} ${className}`}
      aria-hidden
    />
  );
};

function TagFlags({ tag, className = '' }: { tag: string; className?: string }) {
  const flags = getTagCountryIso2List(tag);
  if (!flags.length) return null;
  return (
    <span className={`inline-flex shrink-0 items-center ${className}`} aria-hidden>
      {flags.map((iso2, index) => (
        <CountryFlag
          key={`${iso2}-${index}`}
          iso2={iso2}
          shape="circle"
          className={index > 0 ? '-ml-1' : ''}
        />
      ))}
    </span>
  );
}

type TagBadgeProps = {
  tag: string;
  className?: string;
  /** `tile` — colored gradient pill; `text` — plain label (e.g. journey title). */
  variant?: 'tile' | 'text';
};

export const TagBadge: React.FC<TagBadgeProps> = ({
  tag,
  className = '',
  variant = 'tile',
}) => {
  const [, setTick] = useState(0);
  useEffect(() => subscribeTagAppearance(() => setTick((n) => n + 1)), []);

  if (variant === 'text') {
    return (
      <span className={`inline-flex max-w-full items-center gap-1.5 truncate ${className}`} title={tag}>
        <TagFlags tag={tag} />
        <span className="min-w-0 truncate">{tag}</span>
      </span>
    );
  }

  const meta = getTagIconMeta(getTagIconId(tag));

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-md px-2 py-0.5 text-[11px] font-medium leading-snug ring-1 ring-inset ring-black/5 dark:ring-white/10 ${meta.chipClass} ${meta.iconClass} ${className}`}
      title={tag}
    >
      <TagFlags tag={tag} />
      <span className="min-w-0 truncate">{tag}</span>
    </span>
  );
};
